import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DroneDTO, deleteDroneById } from "../../store/drone_slice";
import { AppDispatch } from "../../store/store";
import { selectAuth } from "../../store/auth_slice";
import { bookDrone } from "../../store/whatsApp_slice";
import InformationModal from "../../common/modal/information_modal";
import Title from "../../common/title/title";
import DateRangePicker from "./calendar/date_range_picker";
import Button from "../../common/button/button";
import DropdownMenu from "./dropdown/dropdown_drone";

import ToastService from "../../common/toast/toast_service";

interface DroneInfoProps {
  droneData: DroneDTO;
}

const DroneInfo: React.FC<DroneInfoProps> = ({ droneData }) => {
  const {
    id,
    name: droneName,
    location,
    dateTime: timestamp,
    price: pricePerDay,
    description,
    extraDetails,
    postedBy,
  } = droneData;

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user: authUser } = useSelector(selectAuth);

  const [isOwner, setIsOwner] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const ownershipCheck = Boolean(authUser?.id && postedBy?.id && authUser.id === postedBy.id);
    
    setIsOwner(ownershipCheck);
  }, [authUser, postedBy]);
  
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  /** Remove selectedDateRange if it's not used anymore */
  const [, setTotalPrice] = useState<number>(0);
  // const [isDeleting, setIsDeleting] = useState(false);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  const formattedDate = formatDate(timestamp);

  const handleDateRangeChange = (dateRange: Date[]) => {
    if (dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      const start = dateRange[0].toISOString().split("T")[0] || "";
      const end = dateRange[1].toISOString().split("T")[0] || "";
      setStartDate(start);
      setEndDate(end);
    } else {
      setStartDate("");
      setEndDate("");
    }
    if (dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      const days =
        (dateRange[1].getTime() - dateRange[0].getTime()) /
          (1000 * 60 * 60 * 24) +
        1;
      setTotalPrice(days * pricePerDay);
    } else {
      setTotalPrice(0);
    }
  };

  const handleRentOut = async () => {
    // Check if user is authenticated
    if (!authUser) {
      setIsInfoModalOpen(true);
      return;
    }

    try {
      const response = await dispatch(bookDrone(id, startDate, endDate));
      if (response?.whatsappUrl) {
        // On mobile Safari and other browsers, some popup blockers will block window.open inside async context.
        // On mobile, use direct assignment for cross-browser best compatibility.
        const isMobile =
          /iphone|ipod|ipad|android|blackberry|windows phone|opera mini|mobile/i.test(
            navigator.userAgent
          );
        if (isMobile) {
          window.location.href = response.whatsappUrl;
        } else {
          window.open(response.whatsappUrl, '_blank');
        }
      }
    } catch {
      // Error handling without console logging
    }
  };

  const handleEdit = () => {
    if (!isOwner) return;
    navigate(`/drone-service/${id}`);
  };

  const handleDelete = () => {
    if (!isOwner) return;
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await dispatch(deleteDroneById(id));
      ToastService.showSuccess("Drone deleted successfully!");
      setShowDeleteModal(false);
      navigate("/");
    } catch {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-start">
        <Title
          title={droneName || "Unnamed Drone"}
          subtitle={`${location || "Unknown Location"} - ${formattedDate}`}
        />
        {isOwner && (
          <div className="relative">
            <DropdownMenu onEdit={handleEdit} onDelete={handleDelete} disabled={isDeleting} />
          </div>
        )}
      </div>
      <div className="mb-4">
        <span className="text-xl p-2 rounded-lg font-ubuntu font-bold bg-grayThree">
          {droneData.currency}{pricePerDay || 0}
          <span className="text-base rounded-lg font-ubuntu font-thin bg-grayThree">
            /hour
          </span>
        </span>
        <div className="mt-2 text-gray-700 font-ubuntu text-base">
          {typeof droneData.editCost === "number" && (
            <div>
              <b>Edit Cost:</b> {droneData.currency}{' '}
              {droneData.editCost}
            </div>
          )}
          {typeof droneData.travelCost === "number" && (
            <div>
              <b>Travel Cost/km:</b> {droneData.currency}{' '}
              {droneData.travelCost}
            </div>
          )}
        </div>
      </div>
      <DateRangePicker onDateRangeChange={handleDateRangeChange} />
      {/* All rental price and fee lines removed as requested */}
      <Button
        onClick={handleRentOut}
        buttonType="primary"
        className="w-full py-4 mt-4"
        disabled={isDeleting}
      >
        {isDeleting ? "Deleting..." : "Rent Out"}
      </Button>
      <h3 className="text-xl font-semibold mt-4 mb-2">Description</h3>
      <p className="text-gray-600">
        {description || "Description not available."}
      </p>
      <h3 className="text-xl font-semibold mt-4 mb-2">Availability</h3>
      <p className="text-gray-600">
        {extraDetails || "Availability details not provided."}
      </p>
      
      {/* Information Modal for Delete Confirmation */}
      <InformationModal
        isOpen={showDeleteModal}
        onClose={confirmDelete}
        title="Delete Drone Listing"
        message={`Are you sure you want to delete the listing "${droneName}"? This action cannot be undone.`}
        secondaryButton={{
          label: "Cancel",
          onClick: () => setShowDeleteModal(false)
        }}
      />

      {/* Information Modal for Authentication */}
      <InformationModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        title="Login Required"
        message="You need to be logged in to rent a drone. Please log in to continue."
      />
    </div>
  );
};

export default DroneInfo;
